import { Head, Link, router } from "@inertiajs/react"

import { FilterChip, FilterRow } from "@/components/admin/filter-chip"
import { PageHeader } from "@/components/console/page-header"
import { Badge, type StatusTier } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Table, TableCard } from "@/components/ui/table"
import { DoGlyph } from "@/components/ui/wordmark"
import { AdminLayout } from "@/layouts/admin-layout"
import {
  adminMsgFlowRunPath,
  adminMsgFlowRunsPath,
  newAdminMsgFlowRunPath,
} from "@/routes"

type RunStatus =
  | "pending"
  | "active"
  | "paused"
  | "closed_listening"
  | "completed"
  | "terminated"

interface Run {
  id: number
  contact: { name: string; phone: string | null; initials: string }
  definition: { code: string; label: string; version: number }
  status: RunStatus
  enrolled_at: string | null
  last_event_at: string | null
}

interface Props {
  runs: Run[]
  filters: { status: string | null; definition: string | null }
  available_filters: { statuses: RunStatus[]; definitions: string[] }
  counts: { total: number; last_activity_at: string | null }
}

const STATUS_LABEL: Record<RunStatus, string> = {
  pending: "Pending",
  active: "Active",
  paused: "Paused",
  closed_listening: "Closed listening",
  completed: "Completed",
  terminated: "Terminated",
}

const STATUS_COLOR: Record<RunStatus, StatusTier> = {
  pending: "neutral",
  active: "remote",
  paused: "caution",
  closed_listening: "neutral",
  completed: "confirmed",
  terminated: "system",
}

const humanizeDefinition = (code: string) =>
  code.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())

export default function AdminMsgFlowRunsIndex({
  runs,
  filters,
  available_filters,
  counts,
}: Props) {
  const visitWith = (next: Partial<Props["filters"]>) => {
    const data: Record<string, string> = {}
    const merged = { ...filters, ...next }
    if (merged.status) data.status = merged.status
    if (merged.definition) data.definition = merged.definition
    router.visit(adminMsgFlowRunsPath(), {
      data,
      preserveState: true,
      preserveScroll: true,
      only: ["runs", "filters", "counts"],
    })
  }

  const hasActiveFilters = !!(filters.status || filters.definition)

  const tokens = [
    filters.status && {
      label: `status: ${filters.status.replace(/_/g, " ").toLowerCase()}`,
      onClear: () => visitWith({ status: null }),
    },
    filters.definition && {
      label: `definition: ${humanizeDefinition(filters.definition).toLowerCase()}`,
      onClear: () => visitWith({ definition: null }),
    },
  ].filter((token): token is { label: string; onClear: () => void } =>
    Boolean(token),
  )

  return (
    <AdminLayout>
      <Head title="Message Flow Runs" />
      <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 lg:px-8">
        <PageHeader
          crumbs={[{ label: "message flows" }, { label: "runs" }]}
          title="Message flow runs"
          description="Live SMS conversations with school contacts."
          actions={
            <Link href={newAdminMsgFlowRunPath()}>
              <Button color="primary" size="md" iconLeading={DoGlyph}>
                New run
              </Button>
            </Link>
          }
        />

        <div className="flex flex-col gap-3">
          <FilterRow label="Status">
            <FilterChip
              active={!filters.status}
              onClick={() => visitWith({ status: null })}
            >
              All
            </FilterChip>
            {available_filters.statuses.map((status) => (
              <FilterChip
                key={status}
                active={filters.status === status}
                onClick={() => visitWith({ status })}
              >
                {STATUS_LABEL[status]}
              </FilterChip>
            ))}
          </FilterRow>
          <FilterRow label="Definition">
            <FilterChip
              active={!filters.definition}
              onClick={() => visitWith({ definition: null })}
            >
              All
            </FilterChip>
            {available_filters.definitions.map((definition) => (
              <FilterChip
                key={definition}
                active={filters.definition === definition}
                onClick={() => visitWith({ definition })}
              >
                {humanizeDefinition(definition)}
              </FilterChip>
            ))}
          </FilterRow>
        </div>

        <TableCard.Root bleed>
          <TableCard.Caption
            title="Runs"
            count={`${counts.total} ${counts.total === 1 ? "run" : "runs"}`}
            tokens={tokens}
            note="enrolled ↓"
          />
          {runs.length === 0 ? (
            <TableCard.Empty
              action={
                hasActiveFilters && (
                  <Button
                    size="md"
                    color="secondary"
                    onPress={() =>
                      visitWith({ status: null, definition: null })
                    }
                  >
                    Clear filters
                  </Button>
                )
              }
            >
              No runs match these filters. Clear one above, or enrol a contact
              with a new run.
            </TableCard.Empty>
          ) : (
            <>
              <Table aria-label="MsgFlow runs" rows="two-line">
                <Table.Header>
                  <Table.Head
                    id="contact"
                    isRowHeader
                    label="Contact"
                    className="w-full"
                  />
                  <Table.Head id="definition" label="Definition" />
                  <Table.Head id="status" label="Status" />
                  <Table.Head
                    id="last_event"
                    label="Last event"
                    align="right"
                  />
                </Table.Header>
                <Table.Body items={runs}>
                  {(run) => (
                    <Table.Row id={run.id} href={adminMsgFlowRunPath(run.id)}>
                      <Table.Lead
                        sub={
                          run.contact.phone ? (
                            <span className="notation">
                              {run.contact.phone}
                            </span>
                          ) : undefined
                        }
                      >
                        {run.contact.name}
                      </Table.Lead>
                      <Table.Cell>
                        <Badge type="modern" color="gray" size="sm">
                          {run.definition.label}
                        </Badge>
                      </Table.Cell>
                      <Table.Status tier={STATUS_COLOR[run.status]}>
                        {STATUS_LABEL[run.status]}
                      </Table.Status>
                      <Table.When at={run.last_event_at} relative />
                    </Table.Row>
                  )}
                </Table.Body>
              </Table>
              <TableCard.Footer shown={runs.length} total={counts.total} />
            </>
          )}
        </TableCard.Root>
      </div>
    </AdminLayout>
  )
}
